export class CompressedJsonAdapter {
    constructor() {
        this.Prefixes = {
            POINTER_PREFIX: "p:",
            ESCAPED_PREFIX: "e:"
        };

        this.RADIX = 36;

        this.PrefixValues = Object.values(this.Prefixes);

        this.stringConverter = {
            toPointer: (index) =>
                this.Prefixes.POINTER_PREFIX + index.toString(36),
            toEscaped: (raw) => {
                const needsEscape = this.PrefixValues.some((v) =>
                    raw.startsWith(v)
                );
                return needsEscape ? this.Prefixes.ESCAPED_PREFIX + raw : raw;
            },
            fromEscaped: (escaped) => {
                const needsRestore = escaped.startsWith(
                    this.Prefixes.ESCAPED_PREFIX
                );
                return needsRestore
                    ? escaped.slice(this.Prefixes.ESCAPED_PREFIX.length)
                    : escaped;
            },
            fromPointer: (pointer) => {
                return pointer.slice(this.Prefixes.POINTER_PREFIX.length);
            },
            isPointer: (value) => {
                return value.startsWith(this.Prefixes.POINTER_PREFIX);
            }
        };
    }

    compress(src, options = {}) {
        const { reservedKeys = [], reservedValues = [] } = options;
        const MIN_SHARE_STRING_LENGTH = 4;
        const MAX_SHARE_STRING_LENGTH = 512;
        const keysDict = {};
        const knownValuesDict = {};
        const pointers = [];
        const keys = [];
        const wrappers = {};
        const shortKeyFor = (key) => {
            if (key in keysDict) {
                return keysDict[key];
            }
            const keyIndex = keys.length;
            const shortKey = keyIndex.toString(36);
            keysDict[key] = shortKey;
            keys.push(key);
            return shortKey;
        };

        reservedKeys.forEach((reservedKey) => shortKeyFor(reservedKey));
        const stringValueFor = (value, keyPath) => {
            const canBeShared =
                MIN_SHARE_STRING_LENGTH <= value.length &&
                value.length <= MAX_SHARE_STRING_LENGTH;
            if (canBeShared) {
                if (value in knownValuesDict) {
                    if (knownValuesDict[value].shared) {
                        return knownValuesDict[value].pointer;
                    }
                    const index = pointers.length;
                    pointers.push(value);
                    const pointer = this.stringConverter.toPointer(index);
                    knownValuesDict[value].pointer = pointer;
                    knownValuesDict[value].shared = true;
                    return pointer;
                }
            }
            knownValuesDict[value] = { keyPath, shared: false, pointer: null };
            return this.stringConverter.toEscaped(value);
        };
        reservedValues.forEach((reservedValue) =>
            stringValueFor(reservedValue)
        );
        const convert = (values, { keyPath = "" } = {}) => {
            if (Array.isArray(values)) {
                const arr = values.map((v, i) =>
                    convert(v, { keyPath: [keyPath, i].join("/") })
                );
                wrappers[keyPath] = arr;
                return arr;
            }
            if (values === null) {
                return null;
            }
            const type = typeof values;
            switch (type) {
                case "function":
                    return null;
                case "object": {
                    if (values instanceof Date) {
                        return values.toJSON();
                    }
                    const compressed = {};
                    const keys = Object.keys(values);
                    for (const key of keys) {
                        const value = values[key];
                        const shortKey = shortKeyFor(key);
                        compressed[shortKey] = convert(value, {
                            keyPath: [keyPath, shortKey].join("/")
                        });
                    }
                    wrappers[keyPath] = compressed;
                    return compressed;
                }
                case "string":
                    return stringValueFor(values, keyPath);
                default:
                    return values;
            }
        };
        const converted = convert(src);
        for (const { pointer, shared, keyPath } of Object.values(
            knownValuesDict
        )) {
            if (shared && keyPath) {
                const keys = keyPath.split("/");
                const lastKey = keys.pop();
                const wrapper = wrappers[keys.join("/")];
                wrapper[lastKey] = pointer;
            }
        }
        const K = keys.slice(reservedKeys.length);
        const P = pointers.slice(reservedValues.length);
        return {
            ...(K.length > 0 ? { K } : {}),
            ...(P.length > 0 ? { P } : {}),
            _: converted
        };
    }

    decompress(compressed, options = {}) {
        const { reservedKeys = [], reservedValues = [] } = options;
        if (!compressed) {
            return null;
        }
        const { K = [], P = [], _: values } = compressed;
        const keys = [...reservedKeys, ...K];
        const pointers = [...reservedValues, ...P];
        const stringValueFor = (value) => {
            if (this.stringConverter.isPointer(value)) {
                const index = parseInt(
                    this.stringConverter.fromPointer(value),
                    this.RADIX
                );
                return pointers[index];
            }
            return this.stringConverter.fromEscaped(value);
        };
        const convert = (values, { keyPath = "" } = {}) => {
            if (Array.isArray(values)) {
                return values.map((v, i) =>
                    convert(v, { keyPath: [keyPath, i].join("/") })
                );
            }
            if (values === null) {
                return null;
            }
            switch (typeof values) {
                case "undefined": {
                    return;
                }
                case "function":
                    return null;
                case "object": {
                    const decompressed = {};
                    const shortKeys = Object.keys(values);
                    for (const shortKey of shortKeys) {
                        const value = values[shortKey];
                        const key = keys[parseInt(shortKey, this.RADIX)];
                        decompressed[key] = convert(value, {
                            keyPath: [keyPath, shortKey].join("/")
                        });
                    }
                    return decompressed;
                }
                case "string":
                    return stringValueFor(values, keyPath);
                default:
                    return values;
            }
        };

        return convert(values);
    }
}
